import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Zap, Car, Library, Copy, Download, Loader2, Wrench, ArrowRight, Package, Box, Workflow, Boxes, Radio, Lock, LayoutDashboard, KeyRound, Trash2, CheckCircle2, ChevronRight, Palette } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import DevGate from "@/components/DevGate";
import Seo from "@/components/Seo";
import LightbarStudio from "@/components/ncmod/LightbarStudio";
import FGearLab from "@/components/ncmod/FGearLab";
import VehicleStudio from "@/components/ncmod/VehicleStudio";
import LiveryPainter from "@/components/ncmod/LiveryPainter";
import AdminAi3d, { HUB_PRESETS } from "@/components/admin/AdminAi3d";
import BlueprintEditor from "@/components/sdk/BlueprintEditor";
import SandboxTab from "@/components/sdk/SandboxTab";
import CadTab from "@/components/sdk/CadTab";
import api, { formatApiErrorDetail } from "@/lib/api";

const TOOLS = [
  { id: "overview", label: "Suite Overview", kicker: "DASHBOARD", icon: LayoutDashboard, status: "CORE" },
  { id: "lightbar", label: "Lightbar Studio", kicker: "ELS FLASHER", icon: Zap, status: "OPERATIONAL", desc: "Frame-by-frame emergency flash patterns on your real 3D lightbars — orbit, swap housings, export UE5 DataTable CSV." },
  { id: "vehicle", label: "Vehicle Studio", kicker: "PURSUIT SPEC", icon: Car, status: "OPERATIONAL", desc: "Build custom patrol cars: lightbars, visor lights, spotlights, push bars, antennas, rims, tint, stealth paint, sirens and a fully editable interior." },
  { id: "livery", label: "Livery Painter", kicker: "TEXTURING", icon: Palette, status: "BETA", desc: "Stamp decals, department text and striping straight onto the 3D body — bake to a 2048px UV sheet with a skin manifest." },
  { id: "fgear", label: "FGear Vehicle Lab", kicker: "RIGGING", icon: Wrench, status: "OPERATIONAL", desc: "Import vehicle models and rig them for the UE5.1 FGear physics system — wheels, suspension, export INI." },
  { id: "ai3d", label: "AI 3D Generator", kicker: "TRELLIS.2", icon: Box, status: "BYOK", desc: "Image to game-ready textured GLB in minutes. Bring your own fal.ai key — your credits, your models." },
  { id: "blueprint", label: "Mission Blueprint", kicker: "NODE LOGIC", icon: Workflow, status: "OPERATIONAL", desc: "Visual node graph for branching mission logic — triggers, AI moods, SWAT actions. Compiles to JSON + Lua." },
  { id: "sandbox", label: "3D Sandbox", kicker: "PLAYTEST", icon: Boxes, status: "BETA", desc: "Fly through live scenarios and watch your blueprint execute — FOV cones, AI pathing, scrubbable timeline." },
  { id: "cad", label: "CAD Dispatch", kicker: "RADIO NET", icon: Radio, status: "BETA", desc: "A real police CAD console: incident queue, drag-drop unit assignment, AI voice dispatch, tactical map." },
  { id: "library", label: "Community Library", kicker: "REMIX", icon: Library, status: "LIVE", desc: "Every shared pattern, rig and blueprint — fork the community's work and make it yours." },
];

const STATUS_CLS = {
  OPERATIONAL: "text-nc-green border-nc-green/40 bg-nc-green/5",
  LIVE: "text-nc-green border-nc-green/40 bg-nc-green/5",
  BETA: "text-nc-orange border-nc-orange/40 bg-nc-orange/5",
  BYOK: "text-nc-blue border-nc-blue/40 bg-nc-blue/5",
  CORE: "text-zinc-400 border-white/15",
};

function FalKeyCard() {
  const [status, setStatus] = useState(null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/ai3d/key-status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/ai3d/key", { api_key: key });
      setKey("");
      toast.success("Key saved — generations bill your fal.ai account");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="fal-key-card" className="border border-nc-blue/30 bg-nc-blue/5 p-5 flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <KeyRound className="w-4 h-4 text-nc-blue" />
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase">Bring your own fal.ai key</p>
        {status?.has_key && <span className="font-mono text-[8px] text-nc-green border border-nc-green/40 px-2 py-0.5 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> KEY ACTIVE {status.masked}</span>}
      </div>
      <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">
        The studio never pays for your generations. Grab a free key at <a href="https://fal.ai/dashboard/keys" target="_blank" rel="noopener noreferrer" className="text-nc-blue underline underline-offset-4">fal.ai/dashboard/keys</a> — it's stored encrypted and used only for your jobs.
      </p>
      {status && !status.has_key ? (
        <div className="flex gap-2">
          <input data-testid="fal-key-input" type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Paste your fal.ai API key" className="flex-1 bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue" />
          <button data-testid="fal-key-save" onClick={save} disabled={busy || key.trim().length < 20} className="font-mono text-[10px] tracking-[0.15em] uppercase bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white px-4 py-2.5 transition-colors">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save Key"}
          </button>
        </div>
      ) : status?.has_key ? (
        <button data-testid="fal-key-remove" onClick={async () => { await api.delete("/ai3d/key"); toast.success("Key removed"); load(); }} className="self-start font-mono text-[9px] uppercase border border-red-500/40 text-red-400 hover:bg-red-500/10 px-3 py-2 transition-colors flex items-center gap-1.5">
          <Trash2 className="w-3 h-3" /> Remove Key
        </button>
      ) : null}
    </div>
  );
}

function CommunityLibrary() {
  const [lightbar, setLightbar] = useState([]);
  const [fgear, setFgear] = useState([]);
  const [vehicle, setVehicle] = useState([]);
  const [me, setMe] = useState(null);
  const [busyId, setBusyId] = useState("");

  useEffect(() => {
    api.get("/ncmod/lightbar/library").then((r) => setLightbar(r.data.items || [])).catch(() => {});
    api.get("/ncmod/fgear/library").then((r) => setFgear(r.data.items || [])).catch(() => {});
    api.get("/ncmod/vehicle/library").then((r) => setVehicle(r.data.items || [])).catch(() => {});
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const clone = async (kind, id) => {
    if (!me) { toast.error("Sign in to remix shared items"); return; }
    setBusyId(id);
    try {
      await api.post(`/ncmod/${kind}/${id}/clone`);
      toast.success("Remixed into your workshop — open the studio to edit it");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  const Section = ({ title, kind, items, testid }) => (
    <div className="mb-10">
      <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase mb-4">{title}</p>
      {items.length === 0 ? (
        <div className="border border-white/10 border-dashed p-8 text-center font-mono text-[10px] text-zinc-600">Nothing shared yet — be the first. Build in the studio, tick "Share publicly".</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it, i) => (
            <div key={it.id} data-testid={`${testid}-${i}`} className="border border-white/10 bg-black/40 p-5 flex flex-col gap-2.5 hover:border-nc-blue/30 transition-colors group">
              <div className="flex items-start justify-between gap-2">
                <p className="font-sans font-bold text-sm text-zinc-100 group-hover:text-nc-blue transition-colors">{it.name}</p>
                <span className="font-mono text-[8px] text-zinc-500 shrink-0 flex items-center gap-1"><Download className="w-3 h-3" /> {it.downloads}</span>
              </div>
              {it.description && <p className="font-mono text-[10px] text-zinc-500 leading-relaxed line-clamp-2">{it.description}</p>}
              <div className="flex items-center justify-between mt-auto pt-1">
                <span className="font-mono text-[8px] text-zinc-600 uppercase">by {it.author}</span>
                <button data-testid={`clone-${kind}-${i}`} disabled={busyId === it.id} onClick={() => clone(kind, it.id)} className="font-mono text-[8px] tracking-[0.15em] uppercase border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 px-2.5 py-1.5 transition-colors flex items-center gap-1">
                  {busyId === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />} Remix
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="modhub-library">
      <Section title="Vehicle builds — patrol specs" kind="vehicle" items={vehicle} testid="lib-vehicle" />
      <Section title="Lightbar patterns" kind="lightbar" items={lightbar} testid="lib-lightbar" />
      <Section title="FGear vehicle rigs" kind="fgear" items={fgear} testid="lib-fgear" />
    </div>
  );
}

function Overview({ onOpen }) {
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get("/ncmod/lightbar/library").catch(() => ({ data: { items: [] } })),
      api.get("/ncmod/fgear/library").catch(() => ({ data: { items: [] } })),
      api.get("/sdk/missions").catch(() => ({ data: { missions: [] } })),
    ]).then(([lb, fg, ms]) => setCounts({
      lightbar: (lb.data.items || []).length,
      fgear: (fg.data.items || []).length,
      missions: (ms.data.missions || []).length,
    }));
  }, []);

  return (
    <div data-testid="hub-overview" className="flex flex-col gap-8">
      {/* suite status strip */}
      <div className="border border-white/10 bg-black/50 relative overflow-hidden p-6">
        <div className="absolute inset-0 grid-texture opacity-30" />
        <div className="relative flex flex-wrap items-center gap-x-8 gap-y-3">
          <p className="font-mono text-[10px] tracking-[0.35em] text-nc-orange uppercase">Suite Telemetry</p>
          {[["UE5.1 PIPELINE", "text-nc-green"], ["TRELLIS.2 GPU", "text-nc-green"], ["FGear PHYSICS", "text-nc-green"], ["CAD RADIO NET", "text-nc-orange"]].map(([label, cls]) => (
            <span key={label} className="font-mono text-[9px] tracking-[0.2em] flex items-center gap-2 text-zinc-400">
              <span className={`w-1.5 h-1.5 rounded-full ${cls.replace("text-", "bg-")} animate-pulse`} /> {label}
            </span>
          ))}
          {counts && (
            <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 ml-auto">
              COMMUNITY VAULT // {counts.lightbar + counts.fgear + counts.missions} SHARED WORKS
            </span>
          )}
        </div>
      </div>

      {/* tool grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {TOOLS.filter((t) => t.id !== "overview").map((t, i) => (
          <motion.button
            key={t.id}
            data-testid={`hub-tool-card-${t.id}`}
            onClick={() => onOpen(t.id)}
            className="group relative text-left border border-white/10 bg-black/40 p-6 overflow-hidden hover:border-nc-blue/50 transition-colors duration-300"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="absolute -top-14 -right-14 w-36 h-36 bg-nc-blue/10 blur-[60px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="absolute top-0 left-0 h-[2px] w-0 group-hover:w-full bg-gradient-to-r from-nc-blue to-nc-orange transition-all duration-500" />
            <div className="flex items-start justify-between mb-5">
              <div className="w-11 h-11 border border-white/15 bg-[#0a0a0a] flex items-center justify-center group-hover:border-nc-blue/50 transition-colors">
                <t.icon className="w-5 h-5 text-nc-blue" strokeWidth={1.5} />
              </div>
              <span className={`font-mono text-[8px] tracking-[0.2em] border px-2 py-1 ${STATUS_CLS[t.status]}`}>{t.status}</span>
            </div>
            <p className="font-mono text-[8px] tracking-[0.3em] text-zinc-600 uppercase">{t.kicker}</p>
            <p className="font-sans font-bold uppercase text-lg text-zinc-50 mt-1 group-hover:text-nc-blue transition-colors">{t.label}</p>
            <p className="font-mono text-[10px] text-zinc-500 leading-relaxed mt-2 min-h-[45px]">{t.desc}</p>
            <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-nc-orange mt-4 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              Launch tool <ChevronRight className="w-3.5 h-3.5" />
            </span>
          </motion.button>
        ))}
      </div>

      <div className="border border-nc-blue/30 bg-nc-blue/5 p-6 flex flex-col md:flex-row gap-5 items-start">
        <Wrench className="w-5 h-5 text-nc-blue shrink-0" strokeWidth={1.75} />
        <div className="flex-1">
          <p className="font-sans font-bold uppercase text-xs text-zinc-100">How mods get into New Citizen</p>
          <p className="font-mono text-[10px] text-zinc-400 mt-2 leading-relaxed max-w-3xl">
            Export from any tool (UE5 DataTable CSV for patterns, FGear INI for rigs, GLB for AI models, JSON/Lua for
            missions), drop the files into your New Citizen mod folder, and the game loads them at startup.
          </p>
        </div>
        <Link to="/asset-packs" className="font-mono text-[10px] tracking-[0.2em] uppercase border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 px-4 py-2.5 transition-colors flex items-center gap-2 shrink-0">
          <Package className="w-3.5 h-3.5" /> Asset Packs <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

export default function ModHubPage() {
  const [tab, setTab] = useState("overview");
  const [me, setMe] = useState(null);
  const active = TOOLS.find((t) => t.id === tab) || TOOLS[0];

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe(null));
  }, []);

  return (
    <div data-testid="modhub-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo
        title="New Citizen Mod Hub — Lightbar Studio, AI 3D, Mission Blueprints & Vehicle Lab"
        description="The official New Citizen modding suite in your browser: 3D lightbar flash-pattern studio, AI image-to-3D generation, visual mission blueprints, FGear vehicle rigging, 3D playtesting sandbox and CAD dispatch — exporting UE5-ready files."
        path="/modhub"
        keywords="New Citizen mods, UE5 modding tools, FGear vehicle rig, lightbar pattern editor, AI 3D generation, mission blueprint editor, Lua mission export, police CAD simulator, game mod hub"
      />
      <SiteNav />

      <div className="flex-1 flex flex-col lg:flex-row relative">
        <DevGate toolName="The New Citizen Modding Suite">
        <div className="absolute inset-0 grid-texture opacity-20 pointer-events-none" />

        {/* studio rail */}
        <aside className="relative lg:w-72 shrink-0 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#070709]/80 backdrop-blur flex lg:flex-col">
          <div className="hidden lg:block px-5 pt-6 pb-5 border-b border-white/10">
            <p className="font-mono text-[9px] tracking-[0.4em] text-nc-orange uppercase">New Citizen</p>
            <p className="font-sans font-bold uppercase text-lg text-zinc-50 tracking-tight mt-1">Modding Suite</p>
            <p className="font-mono text-[8px] text-zinc-600 mt-1">v4 // browser-native UE5 toolchain</p>
          </div>
          <nav data-testid="modhub-tabs" className="flex lg:flex-col gap-0.5 p-2 lg:p-3 overflow-x-auto lg:overflow-visible w-full">
            {TOOLS.map((t) => (
              <button
                key={t.id}
                data-testid={`modhub-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`relative flex items-center gap-3 px-3.5 py-3 text-left transition-all duration-200 whitespace-nowrap group ${
                  tab === t.id ? "bg-nc-blue/10 text-zinc-50" : "text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.03]"
                }`}
              >
                {tab === t.id && <motion.span layoutId="rail-active" className="absolute left-0 top-0 bottom-0 w-[3px] bg-nc-orange" style={{ boxShadow: "0 0 12px rgba(255,107,26,0.7)" }} />}
                <t.icon className={`w-4 h-4 shrink-0 transition-colors ${tab === t.id ? "text-nc-orange" : "text-zinc-600 group-hover:text-zinc-400"}`} strokeWidth={1.75} />
                <span className="flex-1 min-w-0">
                  <span className="font-mono text-[10px] tracking-[0.08em] uppercase block truncate">{t.label}</span>
                  <span className="hidden lg:block font-mono text-[7px] tracking-[0.25em] text-zinc-700 uppercase">{t.kicker}</span>
                </span>
                <span className={`hidden lg:inline font-mono text-[7px] border px-1.5 py-0.5 ${STATUS_CLS[t.status]}`}>{t.status}</span>
              </button>
            ))}
          </nav>
          <div className="hidden lg:block mt-auto px-5 py-4 border-t border-white/10">
            <p className="font-mono text-[8px] text-zinc-600 leading-relaxed">Exports drop straight into your New Citizen mod folder. Nothing leaves your account until you share it.</p>
          </div>
        </aside>

        {/* workbench */}
        <main className="relative flex-1 min-w-0 flex flex-col">
          {tab !== "overview" && (
            <div className="border-b border-white/10 bg-[#070709]/80 backdrop-blur px-5 sm:px-7 py-4 flex items-center gap-4 flex-wrap">
              <active.icon className="w-5 h-5 text-nc-orange" strokeWidth={1.5} />
              <div className="min-w-0">
                <p className="font-mono text-[8px] tracking-[0.35em] text-zinc-600 uppercase">{active.kicker}</p>
                <h1 className="font-sans font-bold uppercase text-base sm:text-lg text-zinc-50 tracking-tight">{active.label}</h1>
              </div>
              <span className={`font-mono text-[8px] tracking-[0.2em] border px-2 py-1 ${STATUS_CLS[active.status]}`}>{active.status}</span>
              {active.desc && <p className="hidden xl:block font-mono text-[10px] text-zinc-500 ml-auto max-w-md leading-relaxed">{active.desc}</p>}
            </div>
          )}

          <div className="flex-1 px-4 sm:px-6 lg:px-7 py-6">
            <AnimatePresence mode="wait">
              <motion.div key={tab} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}>
                {tab === "overview" && <Overview onOpen={setTab} />}
                {tab === "lightbar" && <LightbarStudio />}
                {tab === "vehicle" && <VehicleStudio me={me} />}
                {tab === "livery" && <LiveryPainter me={me} />}
                {tab === "fgear" && <FGearLab />}
                {tab === "ai3d" && (
                  me ? (
                    <div className="flex flex-col gap-5">
                      <FalKeyCard />
                      <AdminAi3d
                        base="/ai3d"
                        presets={HUB_PRESETS}
                        heading="AI 3D Generator // Microsoft TRELLIS.2"
                        note="Turn any image into a game-ready, fully textured GLB. Generations bill YOUR OWN fal.ai key — the studio never pays for your credits. 10 generations per day. Clean product shots and concept art on plain backgrounds produce the best meshes."
                      />
                    </div>
                  ) : (
                    <div data-testid="ai3d-auth-gate" className="border border-nc-blue/30 bg-nc-blue/5 p-10 text-center flex flex-col items-center gap-4">
                      <Lock className="w-8 h-8 text-nc-blue" strokeWidth={1.5} />
                      <p className="font-sans font-bold uppercase text-lg text-zinc-100">Sign in to use the AI 3D Generator</p>
                      <p className="font-mono text-xs text-zinc-500 max-w-md leading-relaxed">Bring your own fal.ai API key and generate game-ready 3D models from any image — billed to your own account, never the studio's.</p>
                      <Link to="/auth" className="font-mono text-xs tracking-[0.25em] uppercase bg-nc-blue text-white px-6 py-3 hover:bg-nc-blue/80 transition-colors">Enlist / Sign In</Link>
                    </div>
                  )
                )}
                {tab === "blueprint" && <BlueprintEditor me={me} />}
                {tab === "sandbox" && <SandboxTab />}
                {tab === "cad" && <CadTab />}
                {tab === "library" && <CommunityLibrary />}
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
        </DevGate>
      </div>
    </div>
  );
}
